import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Dispatch, HTMLInputTypeAttribute, HTMLProps, SetStateAction, useEffect, useState } from 'react';
import { classes, formatMoney } from 'shared';
import { Form, FormErrors, getError, handleError } from 'types';
import { ZodTypeAny, z } from 'zod';

type InputBase<K> = Omit<HTMLProps<HTMLInputElement>, 'value' | 'prefix' | 'suffix' | 'onChange' | 'form'> & {
  name: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  /** This modifies the container's classes */
  className?: string;
  iClassName?: string;
  lClassName?: string;
  iType?: 'integrated' | 'classic' | 'newDash';
  noHideWithPlaceholder?: boolean;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  clean?: boolean;
  required?: boolean;
  textarea?: boolean;
  money?: boolean;
  padPrefix?: boolean;
  // children?: React.ReactNode;
  spinner?: boolean;
  fKey?: K;
};

type InputLegacyHandler<K> = {
  value: string | number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate: (v: any) => void;
  error?: string | string[] | undefined;
  onUpdateError?: (v: string | undefined) => void;
} & InputBase<K>;

type InputHandler<T extends ZodTypeAny, K> = {
  form: Form<T>;
  setForm: Dispatch<SetStateAction<Form<T>>>;
  errors?: FormErrors<T>;
  setErrors?: Dispatch<SetStateAction<FormErrors<T>>>;
} & InputBase<K>;

type InputProps<T extends ZodTypeAny, K extends keyof z.infer<T> | undefined> = {
  fKey?: K;
} & K extends undefined
  ? InputLegacyHandler<K>
  : InputHandler<T, K>;

const isNew = <T extends ZodTypeAny | undefined, K extends keyof z.infer<T> | undefined>(
  props: InputHandler<T, K> | InputLegacyHandler<K>,
): props is InputHandler<T, K> => {
  return 'fKey' in props;
};

export function Input<T extends ZodTypeAny = ZodTypeAny, K extends keyof z.infer<T> | undefined = undefined>({
  ...props
}: InputProps<T, K>) {
  if (!isNew(props)) return <WrappedInput {...props} />;
  const { fKey, form, setForm, errors, setErrors, ...rest } = props;

  return (
    <WrappedInput
      {...rest}
      value={form[fKey]}
      onUpdate={update}
      error={getError(errors, fKey)}
      onUpdateError={v => setErrors && handleError(setErrors, fKey, v)}
    />
  );

  function update(v: z.infer<T>[K]) {
    setForm(f => ({ ...f, [fKey]: v }));
    if (errors?.[fKey] && setErrors) handleError(setErrors, fKey, v);
  }
}

function WrappedInput<K>({
  name,
  value = '',
  error,
  className,
  iClassName,
  lClassName,
  clean,
  placeholder,
  onUpdate,
  onUpdateError,
  iType,
  noHideWithPlaceholder,
  prefix,
  suffix,
  children,
  type,
  spinner,
  ...props
}: InputLegacyHandler<K>) {
  const [disp, setDisp] = useState(type === 'money' ? '$' : '');

  useEffect(() => {
    if (!disp && value && type === 'phone') handlePhone(String(value));
    if (disp === '$' && value && type === 'money') handleMoneyDisp(String(value));
  }, [value]);

  switch (iType) {
    case 'integrated':
      return renderIntegrated();
    case 'classic':
      return renderClassic();
    case 'newDash':
      return renderNewDash();
    default:
      return renderIntegrated();
  }

  function renderClassic() {
    return (
      <div className={classes(className, 'relative')}>
        <label
          htmlFor={sanitize(name)}
          className={classes('inline-flex select-none text-sm font-medium text-gray-700', lClassName)}>
          {name}
          {children}
        </label>
        <div className="mt-1">
          <input
            {...props}
            type={type !== 'phone' ? type : 'text'}
            className={calculateClassicClassName()}
            name={sanitize(name)}
            id={sanitize(name)}
            value={disp ? disp : value !== 0 ? value || '' : value}
            onChange={e => handleChange(e.target.value)}
            placeholder={placeholder}
            aria-describedby={`${sanitize(name)}-error`}
            onBlur={e => handleBlur(e)}
          />
        </div>
        <span id={`${sanitize(name)}-error`} className="text-sm text-red-600">
          {getError()}
        </span>
      </div>
    );
  }

  function renderNewDash() {
    return (
      <div className={classes(className, 'relative')}>
        <label
          htmlFor={sanitize(name)}
          className={classes('block select-none text-sm font-medium text-gray-700', lClassName)}>
          {name}
        </label>
        <input
          {...props}
          type={type !== 'phone' ? type : 'text'}
          className={calculateNewDashClassName()}
          name={sanitize(name)}
          id={sanitize(name)}
          value={disp ? disp : value !== 0 ? value || '' : value}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholder}
          aria-describedby={`${sanitize(name)}-error`}
          onBlur={e => handleBlur(e)}
        />
        <span id={`${sanitize(name)}-error`} className="text-sm text-red-600">
          {getError()}
        </span>
      </div>
    );
  }

  function renderIntegrated() {
    return (
      <div className={classes(className, 'relative mt-[8px]')}>
        <label
          htmlFor={sanitize(name)}
          className={classes(
            'absolute -top-2 left-2 z-[1] -mt-px inline-flex select-none bg-white px-1 text-xs font-medium text-gray-900',
            lClassName,
            (className?.includes('md:pl-2') || className?.includes('md:px-2')) && 'md:ml-2',
            props.disabled && 'bg-gradient-to-b from-white via-white to-gray-200',
            placeholder && !noHideWithPlaceholder && 'hidden',
            spinner !== undefined && 'pr-10',
          )}>
          {name}
          {children}
        </label>
        <div className="relative">
          {prefix && (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-[10] flex items-center pl-3">
              <span className="text-gray-500">{prefix}</span>
            </div>
          )}
          {suffix && (
            <div className="pointer-events-none absolute inset-y-0 right-2 z-[10] flex items-center pl-3">
              <span className="text-gray-500">{suffix}</span>
            </div>
          )}
          {spinner && (
            <div className="pointer-events-none absolute inset-y-0 right-3 z-[10] flex items-center pl-3">
              <span className="text-gray-500">
                <svg
                  className="h-5 w-5 animate-spin text-green-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373
                    0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0
                    3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            </div>
          )}
          <input
            {...props}
            type={type !== 'phone' ? type : 'text'}
            className={calculateIntegratedClassName()}
            name={sanitize(name)}
            id={sanitize(name)}
            value={disp ? disp : (value ?? '')}
            onChange={e => handleChange(e.target.value)}
            placeholder={placeholder}
            aria-describedby={`${sanitize(name)}-error`}
            onBlur={e => handleBlur(e)}
          />
          {!!getError() && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          )}
        </div>
        <span id={`${sanitize(name)}-error`} className="text-sm text-red-600">
          {getError()}
        </span>
      </div>
    );
  }

  function getError() {
    if (!error) return;
    if (typeof error !== 'string') return error?.[0];
    return error;
  }

  function calculateClassicClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      'block w-full cursor-text appearance-none rounded-md px-3 py-2 placeholder-gray-400 shadow-sm disabled:bg-gray-200 disabled:text-gray-600 sm:text-sm',
    );
  }

  function calculateNewDashClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      'mt-1 block w-full rounded-md border px-3 py-2 shadow-sm disabled:bg-gray-300 disabled:opacity-50 sm:text-sm',
    );
  }

  function calculateIntegratedClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      prefix && 'pl-6',
      suffix && 'pr-8',
      'relative block w-full cursor-text rounded-md border bg-white px-4 py-3 pl-3 text-left placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600',
    );
  }

  function handleChange(v: string) {
    if (type === 'money') return handleMoney(v);
    if (type === 'phone') return handlePhone(v);
    if (type === 'number' && props.max !== undefined && Number(v) > Number(props.max)) return;
    if (type === 'number' && props.min !== undefined && Number(v) < Number(props.min)) return;
    onUpdate(type === 'number' && v !== '' && v !== '-' ? Number(v) : v);
    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement, Element>) {
    const v = e.target.value;

    if (type === 'phone') return validatePhone(v);
    if (type === 'money') return handleMoneyBlur(v);

    if (type === 'number' && props.max !== undefined && Number(v) > Number(props.max)) onUpdate(props.max);
    if (type === 'number' && props.min !== undefined && Number(v) < Number(props.min)) onUpdate(props.min);
    if (type === 'number' && v === '') onUpdate(undefined);
    if (type === 'number' && !isNaN(Number(v))) onUpdate(Number(v));
    else if (type === 'number') onUpdate(0);

    if (props.onBlur) props.onBlur(e);
  }

  function sanitize(name: string) {
    return name.replace(/[^a-zA-Z0-9]/g, '-');
  }

  function handleMoneyBlur(v: string) {
    if (!v.replace(/\D/g, '')) {
      setDisp('$');
      onUpdate(undefined);
      return;
    }

    // TODO: fucking fix money that has more than 2 decimal places (holy fuck what the shit)
    const split = v.split('.');
    const cleanDollars = split[0].replace(/\D/g, '');
    let cleanCents = split[1]?.replace(/\D/g, '') || '00';
    if (cleanCents.length > 2) cleanCents = cleanCents.slice(0, 2);
    if (cleanCents.length === 1) cleanCents = `${cleanCents}0`;
    const moneyVal = Number(`$${cleanDollars}.${cleanCents}`.replace(/\D/g, ''));

    onUpdate(moneyVal);
    handleMoneyDisp(String(moneyVal));

    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function handleMoney(v: string) {
    setDisp(v || '$');
    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function handleMoneyDisp(v: string) {
    if (!v.replace(/\D/g, '')) return setDisp('$');
    const formatted = formatMoney(v);

    if (formatted !== disp) setDisp(formatted);
    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function handlePhone(v: string) {
    if (!v) {
      setDisp('');
      onUpdate('');
      return;
    }
    const s = v.replace(/\D/g, '');
    const phone = `(${s.slice(0, 3)}${s.length > 3 ? ') ' + s.slice(3, 6) : ''}${
      s.length > 6 ? '-' + s.slice(6, 10) : ''
    }`;
    onUpdate(phone.replace(/\D/g, ''));
    setDisp(phone);
    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function validatePhone(phone?: string) {
    if (!phone) return;
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    const valid = phoneRegex.test(String(phone));
    if (!valid) onUpdateError('Invalid phone number');
    return valid;
  }
}
